import { useEffect, useEffectEvent, useState } from 'react'
import { useSnackbar } from 'notistack'

import Api from '../api'

/**
 * This hook loads the device photo or thumbnail based on the provided deviceId and type. This hook is required for loading device photos as the endpoint requires the authentication token.
 * @param deviceId - The ID of the device whose photo is to be loaded.
 * @param shouldLoad - A boolean indicating whether to load the photo. This should be `true` if `device.hasPhoto` is `true`.
 * @param type - The type of photo to load, either 'thumb' or 'photo'.
 * @returns An object containing the photo path and a function to set the photo path, which is useful when overriding it with a local image url when uploading a new image.
 * @example
 * const { photoPath, setPhotoPath } = useLoadDevicePhoto(device.id, device.hasPhoto, 'photo')
 * // Use photoPath to display the device photo
 * // Use setPhotoPath to update the photo path when a new photo is uploaded
 */
const useLoadDevicePhoto = (deviceId: number | undefined, shouldLoad: boolean, type: 'thumb' | 'photo') => {
  const { enqueueSnackbar } = useSnackbar()

  const [photoPath, setPhotoPath] = useState<string | null>(null)
  const hasPhotoPath = !!photoPath

  const loadPhoto = useEffectEvent(async () => {
    try {
      const response = await Api.get(`/api/v2/devices/${deviceId}/${type}`, {}, { isFile: true })
      if (response.ok && response instanceof Response) {
        const blob = await response.blob()
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64String = reader.result
          if (base64String) setPhotoPath(base64String as string)
        }
        reader.readAsDataURL(blob) // Read the file as a data URL
      }
    } catch {
      enqueueSnackbar('Error loading device photo', { variant: 'error' })
    }
  })

  useEffect(() => {
    if (shouldLoad && !hasPhotoPath) loadPhoto()
    if (shouldLoad) {
      loadPhoto()
    }
  }, [deviceId, type, shouldLoad, hasPhotoPath])

  return { photoPath, setPhotoPath }
}

export default useLoadDevicePhoto
